/**
 * Measured benefit — the rules over `GET /programme/measurements` and the decision summary's
 * override figures, as pure functions. bata-ui#148 step 6.5, `docs/adr/3001`.
 *
 * `impact.ts` is what a run SAID its decisions were worth (expected, from stored run rows). This
 * module is what the programme MEASURED (bata-platform#431, ADR 9008/9010): one
 * `BenefitMeasurement` row per measure, market and window, each carrying its evidence state,
 * its observation and pair counts, its parameter set and its caveats. Both surfaces that show a
 * measured figure — the Cockpit's Benefits band and `/strategic/test-vs-control` — read it
 * through here, so the two cannot disagree about what a row means.
 *
 * ── THE RULES ────────────────────────────────────────────────────────────────
 *
 *   * **No number without its evidence state.** `measuredFigure` never hands back a bare
 *     value: a value comes with the state it was measured at, and a state this build does not
 *     recognise is shown as unrecognised rather than guessed into one of the three.
 *   * **`insufficient` is serious, never critical** (ADR 0090). It is a finding — "not yet
 *     measurable" — and not a failure; it is also never filtered out, because a measure missing
 *     from a scorecard reads as "not applicable", which is false.
 *   * **Unsigned until signed.** Ruling R5 (the metric sign-off) is open, and the platform puts
 *     `programme.caveat.definitionsUnsigned` on every estimate until it closes. `signOffOf` reads
 *     the rows, and with no row at all the sign-off is unknown, which is labelled unsigned too:
 *     only rows that exist and carry no such caveat can say the definitions were signed.
 *   * **"Confirmed" only with inference** (DEC-11, DEC-27, bata-ui#166 W0-7). A row the platform
 *     publishes as `confirmed` is shown as confirmed only when it carries the inference that
 *     earns the word — a published confidence interval (`lowerBound`, `upperBound`, platform
 *     v0.50) that excludes zero ({@link carriesInference}). Otherwise it is drawn in the neutral
 *     indicative style, labelled as published-confirmed-without-such-an-interval. The interval
 *     is drawn beside every value that has one.
 *   * **Modelled is never measured.** `recaptured_lost_value` is lost units times a recapture
 *     RATE the parameter set assumes (`recaptureRate`) — a modelled value, not an observation.
 *     The platform says so in `outcomeProvenance`; {@link isModelledRow} reads it (and
 *     {@link MODELLED_MEASURES} for a platform before the field), and {@link measuredFigure}
 *     withholds a modelled row from any "measured" column.
 *   * **Pairs, read where the platform writes them.** A matched-control estimate below its pair
 *     floor is written by `programme._insufficient` with `matchedPairs = 0` and the pair count
 *     in `observations` (bata-platform `services/repositories/programme.py`); a confirmed one
 *     puts the pairs in `matchedPairs`. `pairsOf` reads the right field for each.
 */

import { ApiMalformedProblemError, ApiProblemError } from '@/api';
import type { BenefitMeasurement, OverrideFigures, ParameterSetSummary } from '@/api/generated';
import type { StatusLevel } from '@/primitives';

// ── the vocabulary the platform publishes ──────────────────────────────────

export const EVIDENCE_INSUFFICIENT = 'insufficient';
export const EVIDENCE_INDICATIVE = 'indicative';
export const EVIDENCE_CONFIRMED = 'confirmed';

/** The caveat every estimate carries until ruling R5 signs the definitions (ADR 9010). */
export const CAVEAT_DEFINITIONS_UNSIGNED = 'programme.caveat.definitionsUnsigned';

/** The matched-control difference-in-differences — reqs/08 §8, bata-platform ADR 9008. */
export const MEASURE_MATCHED_UPLIFT = 'availability_uplift';
/** Lost units at size position, valued at realised price, times the recapture rate. */
export const MEASURE_RECAPTURED_VALUE = 'recaptured_lost_value';
export const METHOD_MATCHED_CONTROL = 'programme.method.matchedControlDifferenceInDifferences';
/**
 * Net sales, the pilot doors pooled against their controls pooled, each group's pre-period trend
 * removed — bata-platform ADR 9022 (owner ruling 2026-10-03, issue 435).
 */
export const MEASURE_NET_SALES_UPLIFT = 'net_sales_uplift';
/**
 * Deliberately NOT pair-based for {@link pairsOf}: an insufficient net-sales row keeps the pairs
 * it used in `matchedPairs` (often all of them — "no post month yet"), so reading them against
 * the pair floor would say "23 of 20 pairs" about a row that is short of months, not of pairs.
 */
export const METHOD_GROUP_PRE_TREND = 'programme.method.groupPreTrendCorrectedDifference';

/**
 * Measures whose value is MODELLED — computed from an assumed rate — rather than observed.
 * DEC-11: a modelled value never sits under "measured". `recaptured_lost_value` multiplies lost
 * units by the parameter set's `recaptureRate`, an assumption, and carries
 * `programme.caveat.modelledCounterfactual`.
 */
export const MODELLED_MEASURES: ReadonlySet<string> = new Set([MEASURE_RECAPTURED_VALUE]);

export function isModelledMeasure(measure: string): boolean {
  return MODELLED_MEASURES.has(measure);
}

/** The platform's unit strings. `currency` names no currency code — the row's market does. */
export const UNIT_RATIO = 'ratio';
export const UNIT_CURRENCY = 'currency';

// ── the evidence chip ──────────────────────────────────────────────────────

/**
 * A state this build draws, not one the platform publishes: `confirmed` on a row that carries no
 * inference (DEC-11). Drawn in the indicative style with its own words, so the reader sees both
 * what the platform said and why it is not shown as confirmed.
 */
export const EVIDENCE_CONFIRMED_WITHOUT_INTERVAL = 'confirmedWithoutInterval';

export interface EvidenceChipSpec {
  readonly level: StatusLevel;
  readonly labelKey: string;
}

const CHIP: Readonly<Record<string, EvidenceChipSpec>> = {
  // ADR 0090: insufficient evidence is serious, never critical.
  [EVIDENCE_INSUFFICIENT]: { level: 'serious', labelKey: 'strategic.evidence.insufficient' },
  [EVIDENCE_INDICATIVE]: { level: 'warning', labelKey: 'strategic.evidence.indicative' },
  [EVIDENCE_CONFIRMED]: { level: 'good', labelKey: 'strategic.evidence.confirmed' },
  // DEC-11: the indicative style — neutral, never green — with words that say why.
  [EVIDENCE_CONFIRMED_WITHOUT_INTERVAL]: { level: 'warning', labelKey: 'strategic.evidence.confirmedWithoutInterval' },
};

/** The chip for a published evidence state; an unknown word is its own, stated state. */
export function evidenceChip(state: string): EvidenceChipSpec {
  return CHIP[state] ?? { level: 'warning', labelKey: 'strategic.evidence.unrecognised' };
}

/** The platform's word for an outcome a model computed (bata-platform ADR 9011, W0-7). */
export const PROVENANCE_MODELLED = 'modelled';

/**
 * The row's confidence interval, when it publishes one: both bounds finite and ordered. `null`
 * otherwise — a platform before v0.50, or a row the estimator has not given an interval.
 */
export interface ConfidenceInterval {
  readonly lower: number;
  readonly upper: number;
  /** The interval's level as the platform states it (e.g. 0.95), or `null` when unstated. */
  readonly level: number | null;
  readonly standardError: number | null;
}

const finite = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value);

export function intervalOf(row: BenefitMeasurement): ConfidenceInterval | null {
  const { lowerBound, upperBound } = row;
  if (!finite(lowerBound) || !finite(upperBound) || lowerBound > upperBound) return null;
  return {
    lower: lowerBound,
    upper: upperBound,
    level: finite(row.confidenceLevel) ? row.confidenceLevel : null,
    standardError: finite(row.standardError) ? row.standardError : null,
  };
}

/**
 * Whether a row carries the inference that earns "confirmed" (DEC-11, DEC-27): a published
 * confidence interval that excludes zero. The platform already refuses `confirmed` without one
 * (bata-platform ADR 9011); this build checks it again because a green chip is its own claim,
 * and a row that says `confirmed` over an interval straddling zero is drawn as indicative.
 */
export function carriesInference(row: BenefitMeasurement): boolean {
  const interval = intervalOf(row);
  return interval !== null && (interval.lower > 0 || interval.upper < 0);
}

/**
 * Whether the row's value was computed by a model rather than observed: the platform's
 * `outcomeProvenance`, or — for a platform that predates the field — a measure this build knows
 * to be modelled. Never shown under "measured".
 */
export function isModelledRow(row: BenefitMeasurement): boolean {
  return row.outcomeProvenance === PROVENANCE_MODELLED || isModelledMeasure(row.measure);
}

/** The evidence state a surface draws for a row — the published one, except DEC-11's case. */
export function shownEvidenceState(row: BenefitMeasurement): string {
  if (row.evidenceState === EVIDENCE_CONFIRMED && !carriesInference(row)) return EVIDENCE_CONFIRMED_WITHOUT_INTERVAL;
  return row.evidenceState;
}

// ── choosing the row ───────────────────────────────────────────────────────

/**
 * The newest row of one measure in one market — by `windowEnd`, a row with no window last.
 * `country` null is "every market the read returned": the newest across them, and the row
 * names its own market. Nothing is chosen for a measure the read did not return.
 */
export function newestMeasurement(
  rows: readonly BenefitMeasurement[],
  measure: string,
  country: string | null,
): BenefitMeasurement | null {
  let best: BenefitMeasurement | null = null;
  for (const row of rows) {
    if (row.measure !== measure) continue;
    if (country !== null && row.countryKey !== country) continue;
    const window = row.windowEnd ?? '';
    if (best === null || window > (best.windowEnd ?? '')) best = row;
  }
  return best;
}

/** The newest row of a measure per market, markets sorted — the test-vs-control readout's rows. */
export function newestPerMarket(rows: readonly BenefitMeasurement[], measure: string): readonly BenefitMeasurement[] {
  const markets = [...new Set(rows.filter((row) => row.measure === measure).map((row) => row.countryKey ?? ''))].sort();
  return markets.flatMap((market) => {
    const row = newestMeasurement(
      rows.filter((candidate) => (candidate.countryKey ?? '') === market),
      measure,
      null,
    );
    return row === null ? [] : [row];
  });
}

// ── reading a row ──────────────────────────────────────────────────────────

/** The matched pairs a row rests on — see the module note for why the field differs. */
export function pairsOf(row: BenefitMeasurement): number {
  const pairBased = row.methodKey === METHOD_MATCHED_CONTROL;
  if (pairBased && row.evidenceState === EVIDENCE_INSUFFICIENT && row.matchedPairs === 0) return row.observations;
  return row.matchedPairs;
}

/** Whether a row is a matched-pair estimate, whose sufficiency is counted in pairs. */
export function isPairBased(row: BenefitMeasurement): boolean {
  return row.methodKey === METHOD_MATCHED_CONTROL;
}

/**
 * What one measured figure is, in the states a surface draws. There is no "value" without its
 * `state`, and an `insufficient` row has no value by the platform's own contract.
 */
export type MeasuredFigure =
  | { readonly kind: 'none' }
  | {
    readonly kind: 'insufficient';
    readonly row: BenefitMeasurement;
    /** Pairs for a matched-pair estimate, else `null` and {@link observations} counts. */
    readonly pairs: number | null;
    readonly observations: number;
  }
  | {
    readonly kind: 'measured';
    readonly row: BenefitMeasurement;
    readonly value: number;
    readonly state: string;
    /** The published confidence interval, drawn beside the value; `null` when none. */
    readonly interval: ConfidenceInterval | null;
  }
  | { readonly kind: 'unvalued'; readonly row: BenefitMeasurement; readonly state: string }
  /** A modelled row asked for in a "measured" column: withheld, with its reason (DEC-11). */
  | { readonly kind: 'modelledNotMeasured'; readonly row: BenefitMeasurement };

/**
 * `column` is where the figure will be drawn. In a `measured` column a modelled row is never
 * drawn as a measurement — whatever slot asked for it.
 */
export function measuredFigure(row: BenefitMeasurement | null, column: 'measured' | 'modelled' = 'measured'): MeasuredFigure {
  if (row === null) return { kind: 'none' };
  if (column === 'measured' && isModelledRow(row)) return { kind: 'modelledNotMeasured', row };
  if (row.evidenceState === EVIDENCE_INSUFFICIENT) {
    return {
      kind: 'insufficient',
      row,
      pairs: isPairBased(row) ? pairsOf(row) : null,
      observations: row.observations,
    };
  }
  const state = shownEvidenceState(row);
  if (row.value === null) return { kind: 'unvalued', row, state };
  return { kind: 'measured', row, value: row.value, state, interval: intervalOf(row) };
}

/** Sign-off, read from the rows — see the module note. */
export type SignOff = 'signed' | 'unsigned' | 'unknown';

export function signOffOf(rows: readonly BenefitMeasurement[]): SignOff {
  if (rows.length === 0) return 'unknown';
  return rows.some((row) => (row.caveats ?? []).includes(CAVEAT_DEFINITIONS_UNSIGNED)) ? 'unsigned' : 'signed';
}

/** The pair floor the parameter set in force requires, or `null` when none is published. */
export function pairFloorOf(set: ParameterSetSummary | null | undefined): number | null {
  return set === null || set === undefined ? null : set.minimumMatchedPairs;
}

// ── caveats ────────────────────────────────────────────────────────────────

/**
 * The caveat keys this build has words for. The platform sends the key alone (its ICU
 * arguments are not serialised), so each message is written to stand without them. A key not
 * listed is shown as itself, marked unrecognised, never dropped.
 */
export const CAVEAT_LABEL: Readonly<Record<string, string>> = {
  [CAVEAT_DEFINITIONS_UNSIGNED]: 'strategic.caveat.definitionsUnsigned',
  'programme.caveat.belowMatchedPairFloor': 'strategic.caveat.belowMatchedPairFloor',
  'programme.caveat.pairsDroppedBelowFloor': 'strategic.caveat.pairsDroppedBelowFloor',
  'programme.caveat.pairsDropped': 'strategic.caveat.pairsDropped',
  'programme.caveat.modelledCounterfactual': 'strategic.caveat.modelledCounterfactual',
  'programme.caveat.lostUnitsIsAFloor': 'strategic.caveat.lostUnitsIsAFloor',
  'programme.caveat.observationsUnpriced': 'strategic.caveat.observationsUnpriced',
  'programme.caveat.noRealisedPriceAnywhere': 'strategic.caveat.noRealisedPriceAnywhere',
  'programme.caveat.mdeRecordedAtFreeze': 'strategic.caveat.mdeRecordedAtFreeze',
  'programme.caveat.mdeNotComputedAtFreeze': 'strategic.caveat.mdeNotComputedAtFreeze',
  'programme.caveat.mdeUnreadableAtFreeze': 'strategic.caveat.mdeUnreadableAtFreeze',
  'programme.caveat.mdeIsALevelsUpperBound': 'strategic.caveat.mdeIsALevelsUpperBound',
  // The net-sales readout's group pre-trend correction — bata-platform ADR 9022, issue 435.
  'programme.caveat.groupPreTrendCorrected': 'strategic.caveat.groupPreTrendCorrected',
  'programme.caveat.intervalFromPreTrendResiduals': 'strategic.caveat.intervalFromPreTrendResiduals',
  'programme.caveat.noPostPeriodMonth': 'strategic.caveat.noPostPeriodMonth',
  'programme.caveat.tooFewPreMonthsForATrend': 'strategic.caveat.tooFewPreMonthsForATrend',
  'programme.caveat.groupTotalNotPositive': 'strategic.caveat.groupTotalNotPositive',
  'programme.caveat.trendResidualsDegenerate': 'strategic.caveat.trendResidualsDegenerate',
  // The benefit over the measurement window, split by the frozen arms — bata-platform ADR 9027,
  // issue 553 — and the freeze-record reasons it is now insufficient for, shared with the
  // matched-control readout.
  'programme.caveat.measurementWindowByFrozenArms': 'strategic.caveat.measurementWindowByFrozenArms',
  'programme.caveat.noFrozenArms': 'strategic.caveat.noFrozenArms',
  'programme.caveat.measurementWindowNotOpen': 'strategic.caveat.measurementWindowNotOpen',
  'programme.caveat.treatmentArmHasNoObservations': 'strategic.caveat.treatmentArmHasNoObservations',
  'programme.caveat.controlArmHasNoObservations': 'strategic.caveat.controlArmHasNoObservations',
  'programme.caveat.frozenPairsMissing': 'strategic.caveat.frozenPairsMissing',
  'programme.caveat.frozenPrePeriodMissing': 'strategic.caveat.frozenPrePeriodMissing',
  // The rest of what the platform's programme modules emit — the experiment design's balance
  // and power checks, the readout's interval and arm checks, the override outcome, the forecast
  // and cluster-stability scores. `caveat-census.test.ts` fails when the platform catalogue
  // (`platform-caveat-keys.json`) carries a key this table has no words for.
  'programme.caveat.armBelowStoreFloor': 'strategic.caveat.armBelowStoreFloor',
  'programme.caveat.armStoresAbsentFromPanel': 'strategic.caveat.armStoresAbsentFromPanel',
  'programme.caveat.armWithoutRealisedPrice': 'strategic.caveat.armWithoutRealisedPrice',
  'programme.caveat.armsNotShownBalanced': 'strategic.caveat.armsNotShownBalanced',
  'programme.caveat.balanceCovariatesAreTheMatchingCovariates': 'strategic.caveat.balanceCovariatesAreTheMatchingCovariates',
  'programme.caveat.balanceSuppressedBelowArmFloor': 'strategic.caveat.balanceSuppressedBelowArmFloor',
  'programme.caveat.baselineLevelNotPositive': 'strategic.caveat.baselineLevelNotPositive',
  'programme.caveat.baselineVarianceDegenerate': 'strategic.caveat.baselineVarianceDegenerate',
  'programme.caveat.bothArmsRequired': 'strategic.caveat.bothArmsRequired',
  'programme.caveat.categoryAbsentFromBaseline': 'strategic.caveat.categoryAbsentFromBaseline',
  'programme.caveat.covariateArmTooThinForSpread': 'strategic.caveat.covariateArmTooThinForSpread',
  'programme.caveat.covariateSpreadDegenerate': 'strategic.caveat.covariateSpreadDegenerate',
  'programme.caveat.denominatorExcludesSubjects': 'strategic.caveat.denominatorExcludesSubjects',
  'programme.caveat.effectSpreadDegenerate': 'strategic.caveat.effectSpreadDegenerate',
  'programme.caveat.evaluationHorizon': 'strategic.caveat.evaluationHorizon',
  'programme.caveat.intervalAssumesBalancedArms': 'strategic.caveat.intervalAssumesBalancedArms',
  'programme.caveat.intervalIncludesNoEffect': 'strategic.caveat.intervalIncludesNoEffect',
  'programme.caveat.noCommonStoresBetweenRefreshes': 'strategic.caveat.noCommonStoresBetweenRefreshes',
  'programme.caveat.noDisposedRecommendation': 'strategic.caveat.noDisposedRecommendation',
  'programme.caveat.outcomeMeasureUndeclared': 'strategic.caveat.outcomeMeasureUndeclared',
  'programme.caveat.overrideRateIsNeutral': 'strategic.caveat.overrideRateIsNeutral',
  'programme.caveat.overridesExcluded': 'strategic.caveat.overridesExcluded',
  'programme.caveat.overridesUnscorable': 'strategic.caveat.overridesUnscorable',
  'programme.caveat.panelCannotResolveAUsefulLift': 'strategic.caveat.panelCannotResolveAUsefulLift',
  'programme.caveat.readoutParametersMissing': 'strategic.caveat.readoutParametersMissing',
  'programme.caveat.seasonalNaiveIsTheBar': 'strategic.caveat.seasonalNaiveIsTheBar',
  'programme.caveat.stabilityIsNotSufficiency': 'strategic.caveat.stabilityIsNotSufficiency',
  'programme.caveat.storesBelowBaselineWeekFloor': 'strategic.caveat.storesBelowBaselineWeekFloor',
  'programme.caveat.terminalStockDiverged': 'strategic.caveat.terminalStockDiverged',
  'programme.caveat.terminalStockHeld': 'strategic.caveat.terminalStockHeld',
  'programme.caveat.tooFewPairsForAnInterval': 'strategic.caveat.tooFewPairsForAnInterval',
  'programme.caveat.unitsWithoutPriorYear': 'strategic.caveat.unitsWithoutPriorYear',
  'programme.caveat.wapeDenominatorZero': 'strategic.caveat.wapeDenominatorZero',
  // Terminal units and realised price on the frozen arms — bata-platform ADR 9030, issue 524.
  'programme.caveat.terminalStockToleranceMissing': 'strategic.caveat.terminalStockToleranceMissing',
  'programme.caveat.realisedPriceWithoutInterval': 'strategic.caveat.realisedPriceWithoutInterval',
  'programme.caveat.terminalStockGroupTotalNotPositive': 'strategic.caveat.terminalStockGroupTotalNotPositive',
  // ADR 9030 amended 2026-10-12: the joint condition bounds the trend-corrected terminal_units
  // effect, and an unavailable effect refuses the realised-price figure by name.
  'programme.caveat.terminalUnitsEffectUnavailable': 'strategic.caveat.terminalUnitsEffectUnavailable',
};

export const CAVEAT_UNRECOGNISED = 'strategic.caveat.unrecognised';

export interface CaveatLine {
  readonly key: string;
  readonly values: Readonly<Record<string, string>>;
}

/** A row's caveats as catalogue lines, in the platform's order, duplicates dropped. */
export function caveatLines(row: BenefitMeasurement): readonly CaveatLine[] {
  return [...new Set(row.caveats ?? [])].map((caveat) => {
    const key = CAVEAT_LABEL[caveat];
    return key === undefined ? { key: CAVEAT_UNRECOGNISED, values: { caveat } } : { key, values: {} };
  });
}

/** Whether any of the row's caveats speaks to the minimum detectable effect. */
export function hasMdeCaveat(row: BenefitMeasurement): boolean {
  return (row.caveats ?? []).some((caveat) => caveat.startsWith('programme.caveat.mde'));
}

// ── how planners decided ───────────────────────────────────────────────────

/**
 * reqs/07 §3.5 conformance test (c): "a sustained override rate >40% indicates a bad model, <2%
 * indicates rubber-stamping; both raise an exception". The band is programme doctrine, not a
 * parameter this build could tune; the platform publishes the rate unsigned (ruling R5) and the
 * band is stated beside it, never used to colour the rate as good.
 */
export const OVERRIDE_BAND = { low: 0.02, high: 0.4 } as const;

export type OverrideReading =
  | { readonly kind: 'notPublished' }
  | { readonly kind: 'nothingDecided'; readonly deferred: number }
  | {
    readonly kind: 'rate';
    readonly rate: number;
    readonly decided: number;
    readonly overridden: number;
    readonly deferred: number;
    readonly band: 'inside' | 'below' | 'above';
  };

/**
 * One queue's override figures. `undefined` is a platform that predates them (before v0.49) —
 * the type says required because the pinned spec does, so presence is tested here.
 */
export function overrideReading(figures: OverrideFigures | undefined): OverrideReading {
  if (figures === undefined || figures === null) return { kind: 'notPublished' };
  if (figures.rate === null) return { kind: 'nothingDecided', deferred: figures.deferred };
  const band = figures.rate < OVERRIDE_BAND.low ? 'below' : figures.rate > OVERRIDE_BAND.high ? 'above' : 'inside';
  return {
    kind: 'rate', rate: figures.rate, decided: figures.decided, overridden: figures.overridden,
    deferred: figures.deferred, band,
  };
}

/** The outcome's absence reason, as the platform names it. */
export const OUTCOME_NOT_OBSERVED = 'outcome_not_observed';

// ── the cohort ─────────────────────────────────────────────────────────────

/**
 * Whether the pilot cohort is frozen, as a read says. No read publishes the freeze: bata-platform
 * #431 writes it as registry rows through `programme_cohort_freeze` and serves no cohort route, and
 * the freeze itself waits on the 23rd pilot key (bata-data#435 item 49). So the answer is
 * "not published", and `/strategic/test-vs-control` keeps its no-control-group warning — it is
 * dropped only when a read says frozen, never on this build's say-so.
 */
export type CohortStanding = 'frozen' | 'notFrozen' | 'notPublished';
export const COHORT_STANDING: CohortStanding = 'notPublished';

// ── a platform without the read ────────────────────────────────────────────

/**
 * "Not published by this platform version": FastAPI's own 404 (no problem document), the
 * skeleton's `platform.not_found`, or a 501. A `planning.*` 404 is a refusal about the request and
 * is not read as absence. The in-season chapter's `isNotPublished`, restated because a chapter may
 * not import another chapter.
 */
export function isRouteAbsent(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404 || error.status === 501;
  if (!(error instanceof ApiProblemError)) return false;
  if (error.status === 501) return true;
  return error.status === 404 && !error.code.startsWith('planning.');
}
